// AlertMessage.js
import React, { useState, useEffect, useRef } from 'react';
import { FiCheck, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';

const AlertMessage = ({ 
  type = 'success', // 'success', 'error', 'warning', 'info'
  message,
  title,
  duration = 0, // Auto-dismiss after milliseconds (0 for no auto-dismiss)
  onDismiss,
  showCloseButton = true
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Keep a ref to the latest onDismiss so the timer always calls the
  // current version without needing to be in the effect's dependency array.
  // This prevents parent re-renders (which produce a new onDismiss reference)
  // from restarting the timer.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // Auto-dismiss after duration — only depends on duration, runs once on mount.
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onDismissRef.current) onDismissRef.current();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  if (!isVisible) return null;

  const config = {
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-500',
      icon: <FiCheck className="w-6 h-6" />,
      title: title || 'Success!'
    },
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-500',
      icon: <FiAlertCircle className="w-6 h-6" />,
      title: title || 'Error!'
    },
    warning: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-500',
      icon: <FiAlertCircle className="w-6 h-6" />,
      title: title || 'Warning!'
    },
    info: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-500',
      icon: <FiInfo className="w-6 h-6" />,
      title: title || 'Information'
    }
  };

  const currentConfig = config[type];

  return (
    <div className="fixed top-4 left-4 right-4 z-[9999] sm:left-auto sm:right-6 sm:top-6 sm:w-full sm:max-w-md animate-fade-in-down">
      <div className={`${currentConfig.bgColor} border-l-4 ${currentConfig.borderColor} rounded-lg shadow-lg p-4`}>
        <div className="flex items-start">
          <div className={`flex-shrink-0 ${currentConfig.iconColor} mr-3`}>
            {currentConfig.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold ${currentConfig.textColor}`}>
              {currentConfig.title}
            </h3>
            <div className={`mt-1 text-sm ${currentConfig.textColor} break-words`}>
              {message}
            </div>
          </div>
          {showCloseButton && (
            <button
              onClick={() => {
                setIsVisible(false);
                if (onDismiss) onDismiss();
              }}
              className={`flex-shrink-0 ml-4 ${currentConfig.textColor} hover:opacity-75 focus:outline-none`}
            >
              <FiX className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Progress bar for auto-dismiss */}
        {duration > 0 && (
          <div className="mt-2">
            <div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'} animate-progress`}
                style={{ animationDuration: `${duration}ms` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertMessage;